import { FORMULA_EVALUATOR_GENERATED_VARIABLES } from './formula-evaluator';

interface MultiselectFieldLike {
  field_id?: unknown;
  field_type?: unknown;
}

interface DynamicWidgetLike {
  fields?: unknown;
}

function parseDynamicWidget(dynamicWidget: unknown): DynamicWidgetLike | null {
  if (typeof dynamicWidget === 'string') {
    try {
      const parsed: unknown = JSON.parse(dynamicWidget);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as DynamicWidgetLike
        : null;
    } catch {
      return null;
    }
  }

  return dynamicWidget !== null && typeof dynamicWidget === 'object' && !Array.isArray(dynamicWidget)
    ? dynamicWidget as DynamicWidgetLike
    : null;
}

function parseMultiselectValue(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return trimmed.split(',');
}

function countDistinctValues(value: unknown): number {
  const normalized = parseMultiselectValue(value)
    .filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
    .map(item => String(item).trim())
    .filter(Boolean);

  return new Set(normalized).size;
}

/**
 * Build trusted `<field_id>_count` variables for pricing formulas.
 * Malformed widget definitions and input values are treated as empty rather than
 * allowing pricing or route estimation to fail.
 */
export function buildMultiselectCountContext(
  inputParameters: Record<string, unknown> | null | undefined,
  dynamicWidget: unknown
): Record<string, number> {
  const widget = parseDynamicWidget(dynamicWidget);
  if (!widget || !Array.isArray(widget.fields)) return {};

  const counts: Record<string, number> = {};
  const fieldIds = new Set(
    widget.fields.flatMap(candidate => {
      if (candidate === null || typeof candidate !== 'object') return [];
      const fieldId = (candidate as MultiselectFieldLike).field_id;
      return typeof fieldId === 'string' && fieldId ? [fieldId] : [];
    })
  );

  for (const candidate of widget.fields) {
    if (candidate === null || typeof candidate !== 'object') continue;
    const field = candidate as MultiselectFieldLike;
    if (field.field_type !== 'multiselect' || typeof field.field_id !== 'string' || !field.field_id) {
      continue;
    }

    const countName = `${field.field_id}_count`;
    if (FORMULA_EVALUATOR_GENERATED_VARIABLES.has(countName) || fieldIds.has(countName)) continue;

    counts[countName] = countDistinctValues(inputParameters?.[field.field_id]);
  }

  return counts;
}
